import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Search,
  Copy,
  RefreshCw,
  Zap,
  Globe,
  CheckCircle,
  AlertCircle,
  Layout,
  MousePointer2,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../firebase.init";
import { validateGeminiKey } from "../../../utils/geminiUtils";

const MetaGenerator = () => {
  // ==========================================
  // 1. STATE MANAGEMENT
  // ==========================================
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // User Data & Credits

  const credits = "Unlimited";

  // Form State
  const [url, setUrl] = useState("");
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [tone, setTone] = useState("SEO Optimized (High CTR)");

  // Generated Content State (Array of options)
  const [results, setResults] = useState([]);

  // Options
  const toneOptions = [
    "SEO Optimized (High CTR)",
    "Professional / Corporate",
    "Creative / Viral",
    "Question Based (Educational)",
  ];

  // ==========================================
  // 2. BACKEND / CREDIT LOGIC (UNCHANGED)
  // ==========================================

  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setLoading(false);
        return;
      }
      try {
const userResponse = await axios.get(`https://server.enjoywiki.com/youtubeai-may/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
        }
      } catch (err) {
        setError("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);  // ==========================================
  // 3. AI GENERATION LOGIC
  // ==========================================

  const constructPrompt = () => {
    return `You are a world-class SEO Expert. Generate 3 distinct sets of Meta Titles and Meta Descriptions based on the following:

    Topic/Context: ${topic}
    ${url ? `Target URL: ${url}` : ""}
    Target Keywords: ${keywords}
    Style/Tone: ${tone}

    STRICT CONSTRAINTS:
    1. **Title:** Must be between 55-60 characters (including spaces). Urgent and clickable.
    2. **Description:** Must be between 130-155 characters. clearly summarizing the page.
    3. Include the target keyword naturally.
    
    OUTPUT FORMAT:
    You must output strictly in this JSON format (no markdown code blocks):
    [
      {"title": "Title 1 here", "desc": "Description 1 here"},
      {"title": "Title 2 here", "desc": "Description 2 here"},
      {"title": "Title 3 here", "desc": "Description 3 here"}
    ]`;
  };

  const generateMeta = async () => {
    if (!topic.trim()) {
      toast.error("Please enter a topic or page context.");
      return;
    }

    setGenerating(true);
    setError("");
    setResults([]);

    try {
      // 1. Deduct Credit

      // 2. Call AI
            const apiKey = await validateGeminiKey(user.email);
      if (!apiKey) {
        setGenerating(false);
        return;
      }
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${localStorage.getItem("geminiTextModel") || "gemini-2.5-flash-lite"}:generateContent?key=${apiKey}`;

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: constructPrompt() }] }],
          generationConfig: { temperature: 0.7, maxOutputTokens: 1024 },
        }),
      });

      if (!response.ok) throw new Error("AI Generation Failed");

      const data = await response.json();
      const textResult = data.candidates?.[0]?.content?.parts?.[0]?.text;

      // Parse JSON Output
      try {
        // Cleanup markdown if AI adds it
        const cleanJson = textResult
          .replace(/```json/g, "")
          .replace(/```/g, "")
          .trim();
        const parsed = JSON.parse(cleanJson);
        if (Array.isArray(parsed)) {
          setResults(parsed);
          toast.success("Meta tags generated!");
        } else {
          throw new Error("Invalid format");
        }
      } catch (parseError) {
        console.error("Parsing failed", textResult);
        throw new Error("AI produced invalid format. Please try again.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message);
      setError(err.message);

      // Refund Logic
      try {
        toast("Credits refunded.");
      } catch (refundErr) {
        console.error(refundErr);
      }
    } finally {
      setGenerating(false);
    }
  };

  // ==========================================
  // 4. UTILS & UI HELPERS
  // ==========================================
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied!"));
  };

  // Character Count Color Logic
  const getLenColor = (len, type) => {
    if (type === "title") {
      if (len > 60) return "text-danger"; // Too long
      if (len < 30) return "text-warning"; // Too short
      return "text-success"; // Perfect
    } else {
      if (len > 160) return "text-danger";
      if (len < 100) return "text-warning";
      return "text-success";
    }
  };

  // ==========================================
  // 5. RENDER UI
  // ==========================================

  if (loadingAuth || loading ) {
    return (
      <div className="loader-wrapper">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-3 text-muted fw-medium">Loading SEO Tool...</p>
        <style jsx>{`
          .loader-wrapper {
            height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background: #f8fafc;
          }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-wrapper">
        <div className="auth-card text-center">
          <div
            className="mb-4 icon-box mx-auto bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: 60, height: 60 }}
          >
            <Search size={30} />
          </div>
          <h2 className="fw-bold text-dark mb-2">SEO Meta Generator</h2>
          <p className="text-muted mb-4">
            Log in to optimize your search rankings.
          </p>
          <button
            className="btn btn-primary btn-lg w-100 fw-semibold"
            onClick={() => (window.location.href = "/login")}
          >
            Sign In
          </button>
        </div>
        <style jsx>{`
          .auth-wrapper {
            height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f1f5f9;
          }
          .auth-card {
            background: white;
            padding: 2.5rem;
            border-radius: 16px;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
            max-width: 400px;
            width: 90%;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Navbar */}
      <nav className="navbar sticky-top navbar-expand-lg bg-white/80 backdrop-blur border-bottom">
        <div className="container-xl">
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-icon btn-sm btn-light"
              onClick={() => window.history.back()}
            >
              &larr;
            </button>
            <span className="navbar-brand fw-bold d-flex align-items-center gap-2 text-slate-800">
              <div className="brand-icon">
                <Search size={18} color="white" />
              </div>
              MetaSEO
            </span>
            <div className="credit-pill">
              <Zap size={14} fill="currentColor" />
              <span>Unlimited Credits</span>
            </div>
          </div>
        </div>
      </nav>

      <main className="container-xl py-4 py-lg-5">
        <div className="row g-4">
          {/* LEFT: Inputs */}
          <div className="col-lg-4">
            <div className="saas-card h-100">
              <div className="card-header-custom">
                <Layout size={18} className="text-primary" />
                <h5 className="mb-0 fw-semibold text-slate-700">
                  Page Details
                </h5>
              </div>

              <div className="p-4">
                {/* Topic Input */}
                <div className="mb-4">
                  <label className="form-label-custom">
                    Topic / Page Content
                  </label>
                  <textarea
                    className="form-control form-control-custom"
                    rows="4"
                    placeholder="e.g. A blog post about the benefits of green tea for weight loss..."
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                  />
                </div>

                {/* Keywords */}
                <div className="mb-4">
                  <label className="form-label-custom">Target Keywords</label>
                  <input
                    type="text"
                    className="form-control form-control-custom"
                    placeholder="e.g. green tea benefits, weight loss"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                  />
                </div>

                {/* Tone */}
                <div className="mb-4">
                  <label className="form-label-custom">Tone Strategy</label>
                  <select
                    className="form-select form-select-custom"
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                  >
                    {toneOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Generate Button */}
                <button
                  className="btn btn-primary-gradient w-100"
                  onClick={generateMeta}
                  disabled={generating || !topic.trim()}
                >
                  {generating ? (
                    <>
                      <RefreshCw className="spin me-2" size={18} />{" "}
                      Optimizing...
                    </>
                  ) : (
                    <>
                      <Zap size={18} className="me-2" fill="currentColor" />{" "}
                      Generate
                    </>
                  )}
                </button>

                {error && (
                  <div className="mt-3 text-danger small bg-danger bg-opacity-10 p-2 rounded d-flex align-items-center">
                    <AlertCircle size={14} className="me-2" /> {error}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Output (SERP Preview) */}
          <div className="col-lg-8">
            <div className="saas-card h-100 d-flex flex-column">
              <div className="card-header-custom justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <MousePointer2 size={18} className="text-primary" />
                  <h5 className="mb-0 fw-semibold text-slate-700">
                    Google Search Preview
                  </h5>
                </div>
                {results.length > 0 && (
                  <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">
                    3 Variations Generated
                  </span>
                )}
              </div>

              <div className="card-body p-0 flex-grow-1 position-relative bg-paper">
                {generating ? (
                  <div className="state-container">
                    <div
                      className="spinner-grow text-primary mb-3"
                      role="status"
                      style={{ width: "3rem", height: "3rem" }}
                    ></div>
                    <h5 className="text-slate-700">Analyzing SERP...</h5>
                    <p className="text-muted small">
                      Checking lengths and keyword density.
                    </p>
                  </div>
                ) : results.length > 0 ? (
                  <div className="output-scroll p-4">
                    {results.map((item, idx) => (
                      <div key={idx} className="serp-card mb-4">
                        {/* Google Simulator View */}
                        <div className="google-preview">
                          <div className="google-url-row">
                            <div className="site-icon">
                              {url ? (
                                <img
                                  src={`https://www.google.com/s2/favicons?domain=${url}`}
                                  alt=""
                                />
                              ) : (
                                <Globe size={14} />
                              )}
                            </div>
                            <div className="url-text">
                              <span className="site-name">
                                {url
                                  ? new URL(
                                      "http://" +
                                        url
                                          .replace("http://", "")
                                          .replace("https://", ""),
                                    ).hostname
                                  : "YourSite.com"}
                              </span>
                              <span className="site-url">
                                {url || "https://www.yoursite.com/slug"}
                              </span>
                            </div>
                          </div>
                          <div className="google-title">{item.title}</div>
                          <div className="google-desc">{item.desc}</div>
                        </div>

                        {/* Controls & Metrics */}
                        <div className="meta-controls">
                          <div className="metric-row">
                            <div className="metric">
                              <span className="label">Title</span>
                              <div className="progress-custom">
                                <div
                                  className={`bar ${getLenColor(item.title.length, "title").replace("text-", "bg-")}`}
                                  style={{
                                    width: `${Math.min((item.title.length / 70) * 100, 100)}%`,
                                  }}
                                ></div>
                              </div>
                              <span
                                className={`count ${getLenColor(item.title.length, "title")}`}
                              >
                                {item.title.length} / 60
                              </span>
                            </div>
                            <button
                              className="btn-copy-sm"
                              onClick={() => copyToClipboard(item.title)}
                            >
                              <Copy size={14} /> Title
                            </button>
                          </div>

                          <div className="metric-row mt-2">
                            <div className="metric">
                              <span className="label">Desc</span>
                              <div className="progress-custom">
                                <div
                                  className={`bar ${getLenColor(item.desc.length, "desc").replace("text-", "bg-")}`}
                                  style={{
                                    width: `${Math.min((item.desc.length / 160) * 100, 100)}%`,
                                  }}
                                ></div>
                              </div>
                              <span
                                className={`count ${getLenColor(item.desc.length, "desc")}`}
                              >
                                {item.desc.length} / 160
                              </span>
                            </div>
                            <button
                              className="btn-copy-sm"
                              onClick={() => copyToClipboard(item.desc)}
                            >
                              <Copy size={14} /> Desc
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="state-container opacity-50">
                    <Search size={64} className="text-slate-300 mb-3" />
                    <h5 className="text-slate-500">No Previews Yet</h5>
                    <p className="text-muted small">
                      Enter your page details to visualize search results.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* --- INLINE CSS --- */}
      <style jsx>{`
        /* VARIABLES */
        :root {
          --primary-color: #6366f1;
          --primary-hover: #4f46e5;
          --slate-50: #f8fafc;
          --slate-100: #f1f5f9;
          --slate-200: #e2e8f0;
          --slate-600: #475569;
          --slate-800: #1e293b;
        }

        .app-container {
          background-color: var(--slate-50);
          min-height: 100vh;
          font-family: "Inter", system-ui, sans-serif;
          color: var(--slate-600);
        }

        /* UTILS */
        .text-slate-700 {
          color: #334155;
        }
        .backdrop-blur {
          backdrop-filter: blur(10px);
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        /* BRAND & NAV */
        .brand-icon {
          background: var(--primary-color);
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .btn-icon {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          border: 1px solid var(--slate-200);
          color: var(--slate-600);
          transition: all 0.2s;
        }
        .btn-icon:hover {
          background: var(--slate-100);
          color: var(--slate-800);
        }

        .credit-pill {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 12px;
          background: #fffbeb;
          color: #b45309;
          border: 1px solid #fcd34d;
          border-radius: 99px;
          font-weight: 600;
          font-size: 0.875rem;
        }
        .buy-btn {
          background: #b45309;
          color: white;
          text-decoration: none;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.7rem;
        }

        /* CARD */
        .saas-card {
          background: white;
          border: 1px solid var(--slate-200);
          border-radius: 16px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }
        .card-header-custom {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid var(--slate-100);
          background: white;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        /* FORM */
        .form-label-custom {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 700;
          color: #94a3b8;
          margin-bottom: 0.5rem;
          display: block;
        }
        .form-control-custom,
        .form-select-custom {
          border: 1px solid var(--slate-200);
          border-radius: 10px;
          padding: 0.65rem 0.85rem;
          font-size: 0.95rem;
          background-color: #fff;
          transition: border-color 0.2s;
        }
        .form-control-custom:focus,
        .form-select-custom:focus {
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
          outline: none;
        }
        .input-group-custom {
          position: relative;
        }
        .input-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          pointer-events: none;
          z-index: 10;
        }

        /* BUTTONS */
        .btn-primary-gradient {
          background: linear-gradient(
            135deg,
            var(--primary-color) 0%,
            var(--primary-hover) 100%
          );
          color: white;
          border: none;
          padding: 0.85rem;
          border-radius: 10px;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
          transition: transform 0.2s;
        }
        .btn-primary-gradient:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(99, 102, 241, 0.35);
        }
        .btn-primary-gradient:disabled {
          background: #cbd5e1;
          box-shadow: none;
          cursor: not-allowed;
        }

        /* SERP CARD STYLES */
        .output-scroll {
          height: 100%;
          overflow-y: auto;
          min-height: 400px;
        }
        .bg-paper {
          background-color: #f8fafc;
        }
        .state-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          min-height: 400px;
          text-align: center;
          padding: 2rem;
        }

        .serp-card {
          background: white;
          border: 1px solid var(--slate-200);
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
        }

        /* Google Simulator */
        .google-preview {
          font-family: arial, sans-serif;
          margin-bottom: 15px;
        }
        .google-url-row {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
        }
        .site-icon {
          width: 26px;
          height: 26px;
          background: #f1f3f4;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .site-icon img {
          width: 16px;
          height: 16px;
        }
        .url-text {
          display: flex;
          flex-direction: column;
          line-height: 1.2;
        }
        .site-name {
          font-size: 14px;
          color: #202124;
        }
        .site-url {
          font-size: 12px;
          color: #4d5156;
        }

        .google-title {
          font-size: 20px;
          color: #1a0dab;
          cursor: pointer;
          line-height: 1.3;
          margin-bottom: 3px;
        }
        .google-title:hover {
          text-decoration: underline;
        }
        .google-desc {
          font-size: 14px;
          color: #4d5156;
          line-height: 1.58;
        }

        /* Metric Controls */
        .meta-controls {
          background: #f8fafc;
          padding: 12px;
          border-radius: 8px;
          border: 1px solid #f1f5f9;
        }
        .metric-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .metric {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-grow: 1;
        }
        .label {
          font-size: 11px;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          width: 40px;
        }
        .progress-custom {
          flex-grow: 1;
          height: 6px;
          background: #e2e8f0;
          border-radius: 3px;
          max-width: 150px;
        }
        .progress-custom .bar {
          height: 100%;
          border-radius: 3px;
          transition: width 0.3s;
        }
        .count {
          font-size: 11px;
          font-weight: 600;
          min-width: 60px;
          text-align: right;
        }
        .btn-copy-sm {
          background: white;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          padding: 4px 10px;
          font-size: 12px;
          font-weight: 600;
          color: #475569;
          display: flex;
          align-items: center;
          gap: 5px;
          transition: all 0.2s;
        }
        .btn-copy-sm:hover {
          background: #f1f5f9;
          color: #1e293b;
        }

        /* Bootstrap overrides */
        .text-success {
          color: #16a34a !important;
        }
        .text-warning {
          color: #ca8a04 !important;
        }
        .text-danger {
          color: #dc2626 !important;
        }
        .bg-success {
          background-color: #16a34a !important;
        }
        .bg-warning {
          background-color: #ca8a04 !important;
        }
        .bg-danger {
          background-color: #dc2626 !important;
        }
      `}</style>
    </div>
  );
};

export default MetaGenerator;
